
import { Fragment, useId } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Parsed number, empty string when cleared, or null for partial/invalid input. */
function parseNumericFieldValue(raw: string): number | "" | null {
  if (raw === "") return "";
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
}

function parseBooleanFieldValue(raw: string | boolean): boolean | null {
  if (typeof raw === "boolean") return raw;
  const normalized = String(raw).trim().toLowerCase();
  if (
    normalized === "" ||
    normalized === "false" ||
    normalized === "0" ||
    normalized === "no" ||
    normalized === "off"
  ) {
    return false;
  }
  if (
    normalized === "true" ||
    normalized === "1" ||
    normalized === "yes" ||
    normalized === "on"
  ) {
    return true;
  }
  return null;
}

function coerceIoTFieldValue(
  fieldType: string,
  raw: string | boolean,
  currentValue: any,
): any {
  if (fieldType === "integer") {
    const parsed = parseNumericFieldValue(String(raw));
    if (parsed === null) return currentValue ?? "";
    if (parsed === "") return "";
    return Math.trunc(parsed);
  }
  if (fieldType === "number") {
    const parsed = parseNumericFieldValue(String(raw));
    return parsed === null ? (currentValue ?? "") : parsed;
  }
  if (fieldType === "boolean") {
    const parsed = parseBooleanFieldValue(raw);
    if (parsed === null) {
      return typeof currentValue === "boolean" ? currentValue : false;
    }
    return parsed;
  }
  return raw;
}

interface Properties {
  readonly?: boolean;
  deviceType: any;
  configuration: any;
  setConfiguration: any;
  /** Called on field blur with the latest configuration (blur does not bubble to parents). */
  onPersist?: (configuration: any) => void;
}

export default function IoTDeviceTypeProperties(properties: Properties) {
  const fieldPrefix = useId();
  const { t } = useTranslation();
  // Readable label for a known configuration field; unknown ids stay as the server names them.
  const fieldLabel = (id: string) => t(`assetPropertiesPanel.iot.fields.${id}`, { defaultValue: id });

  const setFieldValue = (
    field: { id: string; type: string },
    raw: string | boolean,
  ) => {
    if (field.type === "integer" || field.type === "number") {
      if (parseNumericFieldValue(String(raw)) === null) return;
    }
    const value = coerceIoTFieldValue(
      field.type,
      raw,
      properties.configuration[field.id],
    );
    properties.setConfiguration({
      ...properties.configuration,
      [field.id]: value,
    });
  };

  const persistFieldOnBlur = (
    field: { id: string; type: string },
    raw: string | boolean,
  ) => {
    if (!properties.onPersist) return;
    const value = coerceIoTFieldValue(
      field.type,
      raw,
      properties.configuration[field.id],
    );
    const next = { ...properties.configuration, [field.id]: value };
    if (value !== properties.configuration[field.id]) {
      properties.setConfiguration(next);
    }
    properties.onPersist(next);
  };

  const allFields: any[] = properties.deviceType?.configuration ?? [];
  // Identification first (id, uid), everything else is connection behaviour.
  const identityFields = allFields.filter((field) => field.id === "id" || field.id === "uid");
  const connectionFields = allFields.filter((field) => field.id !== "id" && field.id !== "uid");

  return <>{properties.deviceType && properties.deviceType.configuration /*&& properties.properties */
    ?
    <div className="min-w-0 flex-1">
      {properties.readonly
        ?
        /* Label/Wert als Beschreibungsliste — auf schmalen Viewports einspaltig,
           statt der früheren festen 200px-Labelspalte. */
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]">
          <dt className="min-w-0 break-words font-bold">{t("assetPropertiesPanel.iot.type")}:</dt>
          <dd className="min-w-0 break-words">{properties.deviceType.id}</dd>
          {properties.deviceType.configuration.map((field: any) => (
            <Fragment key={field.id}>
              <dt className="min-w-0 break-words font-bold max-sm:mt-2">{fieldLabel(field.id)}:</dt>
              <dd className="min-w-0 break-words">
                {properties.configuration ? properties.configuration[field.id] : '-'}
              </dd>
            </Fragment>
          ))}
        </dl>
        :
        <div className="flex min-w-0 flex-col gap-4">
          <div className="flex flex-wrap items-baseline gap-2 text-sm">
            <span className="text-muted-foreground">{t("assetPropertiesPanel.iot.type")}</span>
            <span className="min-w-0 break-words font-medium">{properties.deviceType.id}</span>
          </div>
          {[
            { key: "identification", fields: identityFields },
            { key: "connection", fields: connectionFields },
          ].map((group) => group.fields.length === 0 ? null : (
          <section key={group.key} aria-labelledby={`${fieldPrefix}-${group.key}`} className="flex min-w-0 flex-col gap-3">
          <h4 id={`${fieldPrefix}-${group.key}`} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t(`assetPropertiesPanel.iot.${group.key}`)}
          </h4>
          <div className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {group.fields.map((field: any) => {
              const fieldId = `${fieldPrefix}-${field.id}`;

              switch (field.type) {
                case 'integer':
                case 'number': return (
                  <div key={field.id} className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor={fieldId}>{fieldLabel(field.id)}</Label>
                    <Input
                      id={fieldId}
                      type="number"
                      value={properties.configuration[field.id] ?? ''}
                      onChange={(e) => setFieldValue(field, e.target.value)}
                      onBlur={(e) => persistFieldOnBlur(field, e.target.value)}
                    />
                  </div>
                );
                case 'boolean': return (
                  <div key={field.id} className="flex min-w-0 items-center gap-2 sm:col-span-full">
                    <Checkbox
                      id={fieldId}
                      checked={coerceIoTFieldValue(
                        field.type,
                        properties.configuration[field.id],
                        properties.configuration[field.id],
                      )}
                      onCheckedChange={(checked) => {
                        setFieldValue(field, checked === true);
                      }}
                      onBlur={(e) => {
                        const checked =
                          e.currentTarget.getAttribute("data-state") === "checked";
                        persistFieldOnBlur(field, checked);
                      }}
                    />
                    <Label htmlFor={fieldId} className="cursor-pointer break-words">{fieldLabel(field.id)}</Label>
                  </div>
                );
                case 'string':
                default: return (
                  <div key={field.id} className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor={fieldId}>{fieldLabel(field.id)}</Label>
                    <Input
                      id={fieldId}
                      value={properties.configuration[field.id] ?? ''}
                      onChange={(e) => {
                        const newConfiguration = { ...properties.configuration } as any;

                        newConfiguration[field.id] = e.target.value;

                        properties.setConfiguration(newConfiguration)
                      }}
                      onBlur={(e) => persistFieldOnBlur(field, e.target.value)}
                    />
                  </div>
                );
              }
            })}
          </div>
          </section>
          ))}
        </div>}
    </div>
    :
    <div></div>
  }
  </>;
}
